import {
  Component,
  computed,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  form,
  FormField,
  required,
  email,
  submit,
} from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { M3CardComponent } from '@shared/card/m3-card.component';
import { M3ButtonComponent } from '@shared/button/m3-button.component';
import {
  isPrimaryOAuthEnabled,
  isHiddenOAuthEnabled,
  getPrimaryOAuthButtonLabel,
  isPrimaryOAuthMicrosoft,
} from '@core/auth/oauth/oauth.config';
import { MicrosoftSignInButtonComponent } from '@core/auth/oauth/components/microsoft-sign-in-button.component';
import { SsoSignInButtonComponent } from '@core/auth/oauth/components/sso-sign-in-button.component';
import { projectedSignal } from '@utils/projected-signal';
import {
  AppUpdatesStore,
  AppUpdatesLabels,
  AppUpdatesAria,
} from '@app/features/app-updates';
import { LoginFormStore } from './state/login-form.store';
import { buildVersion } from '../../../environments/version';

@Component({
  selector: 'app-login-layout',
  providers: [LoginFormStore],
  imports: [
    CommonModule,
    FormsModule,
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressBarModule,
    M3CardComponent,
    M3ButtonComponent,
    MicrosoftSignInButtonComponent,
    SsoSignInButtonComponent,
  ],
  template: `
    <div class="login-container">
      <div class="login-content">
        <div class="login-panels">
          <div class="logo-panel">
            <app-m3-card variant="elevated" class="logo-card">
              <img [src]="logoUrl" alt="Company Logo" class="logo-image" />
            </app-m3-card>
          </div>

          <div class="form-panel">
            <app-m3-card variant="filled" class="login-form-card">
              @if (loginFormStore.isLoading()) {
                <mat-progress-bar
                  mode="indeterminate"
                  class="login-progress-bar"
                ></mat-progress-bar>
              }

              <div class="login-header">
                <h2 class="mat-headline-medium login-title">
                  {{ loginTitle }}
                </h2>

                <div class="subtitle-status-container">
                  <!-- Present from the start, so a failed login is announced -->
                  <div
                    class="status-message error-message mat-body-medium"
                    role="alert"
                  >
                    @switch (loginFormStore.loginStatus()) {
                      @case ('oauth-error') {
                        {{ loginFormStore.oauthError() }}
                      }
                      @case ('login-error') {
                        {{ loginFormStore.loginError() }}
                      }
                    }
                  </div>
                  @switch (loginFormStore.loginStatus()) {
                    @case ('loading') {
                      <p
                        class="mat-body-medium login-subtitle loading-subtitle"
                      >
                        Anmeldung läuft...
                      </p>
                    }
                    @case ('idle') {
                      <p class="mat-body-medium login-subtitle">
                        {{ loginSubtitle }}
                      </p>
                    }
                  }
                </div>
              </div>

              <form (ngSubmit)="onSubmit()" class="login-form">
                <mat-form-field appearance="outline" class="form-field">
                  <mat-label>E-Mail</mat-label>
                  <input
                    matInput
                    type="email"
                    [formField]="loginForm.email"
                    autocomplete="email"
                    [placeholder]="emailPlaceholder"
                  />
                  <mat-icon matPrefix>mail</mat-icon>
                  @if (
                    loginForm.email().invalid() && loginForm.email().touched()
                  ) {
                    <mat-error> Please enter a valid email address </mat-error>
                  }
                </mat-form-field>

                <mat-form-field appearance="outline" class="form-field">
                  <mat-label>Password</mat-label>
                  <input
                    matInput
                    [type]="showPassword ? 'text' : 'password'"
                    [formField]="loginForm.password"
                    autocomplete="current-password"
                    [placeholder]="passwordPlaceholder"
                  />
                  <mat-icon matPrefix>lock</mat-icon>
                  <button
                    mat-icon-button
                    matSuffix
                    type="button"
                    (click)="togglePasswordVisibility()"
                    [attr.aria-label]="showPasswordLabel"
                    [attr.aria-pressed]="showPassword"
                  >
                    <mat-icon>{{
                      showPassword ? 'visibility_off' : 'visibility'
                    }}</mat-icon>
                  </button>
                  @if (
                    loginForm.password().invalid() &&
                    loginForm.password().touched()
                  ) {
                    <mat-error> Password is required </mat-error>
                  }
                </mat-form-field>

                <div class="button-container">
                  <app-m3-button
                    type="submit"
                    variant="filled"
                    [disabled]="
                      loginForm().invalid() || loginFormStore.isLoading()
                    "
                    class="submit-button"
                  >
                    {{ loginButtonText }}
                  </app-m3-button>
                </div>
              </form>

              @if (showSsoSection()) {
                <div class="divider-container">
                  <div class="divider-line"></div>
                  <span class="divider-text">{{ dividerText }}</span>
                  <div class="divider-line"></div>
                </div>

                <div class="sso-button-container">
                  @if (primaryOAuthEnabled) {
                    @if (primaryOAuthIsMicrosoft) {
                      <app-microsoft-sign-in-button
                        [disabled]="loginFormStore.isLoading()"
                        (buttonClick)="onPrimarySsoLogin()"
                      />
                    } @else {
                      <app-sso-sign-in-button
                        [buttonText]="primaryButtonLabel"
                        [disabled]="loginFormStore.isLoading()"
                        (buttonClick)="onPrimarySsoLogin()"
                      />
                    }
                  }

                  @if (showHiddenSso()) {
                    <app-microsoft-sign-in-button
                      [disabled]="loginFormStore.isLoading()"
                      (buttonClick)="onHiddenSsoLogin()"
                    />
                  }
                </div>
              }

              <p class="mat-body-small terms-text">
                {{ termsText }}<br />
                <a [href]="termsUrl" class="terms-link">{{ termsLinkText }}</a>
                {{ andText }}
                <a [href]="privacyPolicyUrl" class="terms-link">{{
                  privacyPolicyLinkText
                }}</a
                >.
              </p>

              @if (appUpdatesStore.isDownloading()) {
                <div
                  class="app-update-notice downloading"
                  role="status"
                  aria-live="polite"
                  [attr.aria-label]="updateDownloadingAriaLabel"
                >
                  <mat-icon class="app-update-notice-icon spin" aria-hidden="true"
                    >sync</mat-icon
                  >
                  <span>{{ downloadingUpdateLabel }}</span>
                </div>
              } @else if (appUpdatesStore.isUpdateAvailable()) {
                <div
                  class="app-update-notice ready"
                  role="status"
                  aria-live="polite"
                  [attr.aria-label]="updateAvailableAriaLabel"
                >
                  <mat-icon class="app-update-notice-icon" aria-hidden="true"
                    >new_releases</mat-icon
                  >
                  <span>{{ updateReadyLabel }}</span>
                  <button
                    type="button"
                    class="app-update-notice-action"
                    (click)="appUpdatesStore.activateUpdate()"
                  >
                    {{ reloadAppLabel }}
                  </button>
                </div>
              }

              <div class="version-text">{{ buildVersionInfo.version }}</div>
            </app-m3-card>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .login-container {
        display: flex;
        width: 100%;
        min-height: 100vh;
        background: var(--md-sys-color-primary-fixed);
        align-items: center;
        justify-content: center;
        padding: 2.5rem;
        box-sizing: border-box;
      }

      .login-content {
        width: 100%;
        max-width: 56rem;
      }

      .login-panels {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 2rem;
      }

      /* Mobile styles */
      @media (max-width: 768px) {
        .login-container {
          padding: 0;
          padding-top: 2rem;
          align-items: flex-start;
        }

        .login-content {
          width: 100%;
          max-width: 100%;
        }

        .login-panels {
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .logo-panel {
          display: none; /* Hide logo on mobile to save space */
        }

        .form-panel {
          width: 100%;
        }

        .login-form-card {
          width: 100%;
          box-sizing: border-box;
        }

        /* Override m3-card internal padding on mobile */
        app-m3-card.login-form-card ::ng-deep .m3-card {
          padding: 1rem;
        }

        .login-progress-bar {
          border-radius: 12px 12px 0 0; /* Match card's top border radius */
        }

        .login-header {
          margin-bottom: 0.75rem;
        }

        .login-title {
          font-size: 1.375rem;
          margin-bottom: 0.25rem;
        }

        .login-subtitle,
        .status-message span {
          font-size: 0.875rem;
        }

        .subtitle-status-container {
          min-height: 1.25rem;
        }

        .status-message {
          gap: 0.375rem;
        }

        .form-field {
          margin-bottom: 0.5rem;
        }

        .terms-text {
          margin-top: 0.75rem;
          font-size: 0.6875rem;
        }
      }

      /* Extra small mobile styles */
      @media (max-width: 480px) {
        .login-container {
          padding: 0;
          padding-top: 1.5rem;
        }

        /* Override m3-card internal padding for smaller screens */
        app-m3-card.login-form-card ::ng-deep .m3-card {
          padding: 0.875rem;
        }

        .login-title {
          font-size: 1.25rem;
        }

        .login-subtitle,
        .status-message span {
          font-size: 0.8125rem;
        }
      }

      .logo-panel,
      .form-panel {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .logo-card {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 200px;
      }

      .logo-image {
        width: 100%;
        height: 6rem;
        object-fit: contain;
      }

      .login-form-card {
        width: 100%;
        position: relative;
        overflow: visible;
      }

      .login-header {
        text-align: center;
        margin-bottom: 0.75rem;
      }

      .login-title {
        color: var(--md-sys-color-on-surface);
        margin-bottom: 0.5rem;
      }

      .login-subtitle {
        color: var(--md-sys-color-on-surface-variant);
        margin: 0;
      }

      .subtitle-status-container {
        min-height: 1.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      }

      .status-message {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        animation: fadeIn 0.3s ease-in-out;
      }

      .error-message {
        color: var(--md-sys-color-error);
        font-weight: 500;
      }

      .loading-subtitle {
        color: var(--md-sys-color-primary);
      }

      .login-progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        z-index: 1;
      }

      .login-subtitle,
      .status-message {
        animation: fadeIn 0.3s ease-in-out;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .login-form {
        display: flex;
        flex-direction: column;
      }

      .form-field {
        width: 100%;
        margin-bottom: 1rem;
      }

      .button-container {
        width: 100%;
      }

      .submit-button {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .terms-text {
        text-align: center;
        margin-top: 1.5rem;
        color: var(--md-sys-color-on-surface-variant);
      }

      .terms-link {
        color: var(--md-sys-color-primary);
        text-decoration: underline;
        transition: all 0.2s ease;
      }

      .terms-link:hover {
        color: var(--md-sys-color-primary-container);
      }

      .divider-container {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin: 1.5rem 0;
      }

      .divider-line {
        flex: 1;
        height: 1px;
        background: var(--md-sys-color-outline-variant);
      }

      .divider-text {
        color: var(--md-sys-color-on-surface-variant);
        font-size: 0.875rem;
        text-transform: lowercase;
      }

      .sso-button-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
      }

      .version-text {
        color: var(--md-sys-color-surface-container);
      }

      .app-update-notice {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-top: 1rem;
        padding: 0.625rem 0.875rem;
        border-radius: 8px;
        font-size: 0.8125rem;
        animation: fadeIn 0.3s ease-in-out;
      }

      .app-update-notice.downloading {
        color: var(--md-sys-color-on-primary-container);
        background: var(--md-sys-color-primary-container);
      }

      .app-update-notice.ready {
        color: var(--md-sys-color-on-tertiary-container);
        background: var(--md-sys-color-tertiary-container);
      }

      .app-update-notice-icon {
        font-size: 1.125rem;
        height: 1.125rem;
        width: 1.125rem;
        flex-shrink: 0;
      }

      .app-update-notice-action {
        margin-left: auto;
        border: none;
        background: none;
        padding: 0;
        color: inherit;
        font-weight: 600;
        text-decoration: underline;
        cursor: pointer;
      }

      .spin {
        animation: spin 1.5s linear infinite;
      }

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(-360deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .spin {
          animation: none;
        }
      }

      @media (max-width: 768px) {
        .divider-container {
          margin: 1rem 0;
        }
      }
    `,
  ],
})
export class LoginLayoutComponent {
  private readonly location = inject(Location);
  readonly loginFormStore = inject(LoginFormStore);
  readonly appUpdatesStore = inject(AppUpdatesStore);
  buildVersionInfo = buildVersion;

  protected readonly downloadingUpdateLabel =
    AppUpdatesLabels.downloadingUpdate();
  protected readonly updateReadyLabel = AppUpdatesLabels.updateAvailable();
  protected readonly reloadAppLabel = AppUpdatesLabels.reloadApp();
  protected readonly updateDownloadingAriaLabel =
    AppUpdatesAria.updateDownloadingStatus();
  protected readonly updateAvailableAriaLabel =
    AppUpdatesAria.updateAvailableBadge();

  private readonly formData = projectedSignal({
    computation: () => this.loginFormStore.formData(),
    update: (value) => this.loginFormStore.updateFormData(value),
  });

  protected readonly loginForm = form(this.formData, (login) => {
    required(login.email);
    email(login.email);
    required(login.password);
  });
  protected readonly loginTitle = $localize`Willkommen zurück`;
  protected readonly loginSubtitle = $localize`Bitte geben Sie Ihre Anmeldedaten ein`;
  protected readonly loginButtonText = $localize`Anmelden`;
  protected readonly emailPlaceholder = '';
  protected readonly passwordPlaceholder = '';
  protected readonly termsText = $localize`Mit der Anmeldung akzeptieren Sie unsere`;
  protected readonly termsLinkText = $localize`Nutzungsbedingungen`;
  protected readonly andText = $localize`und`;
  protected readonly privacyPolicyLinkText = $localize`Datenschutzerklärung`;
  protected readonly dividerText = $localize`oder`;
  protected readonly showPasswordLabel = $localize`Passwort anzeigen`;

  protected readonly termsUrl = '/terms';
  protected readonly privacyPolicyUrl = '/privacy';

  protected readonly primaryOAuthEnabled = isPrimaryOAuthEnabled();
  protected readonly primaryOAuthIsMicrosoft = isPrimaryOAuthMicrosoft();
  protected readonly hiddenOAuthEnabled = isHiddenOAuthEnabled();
  protected readonly primaryButtonLabel = getPrimaryOAuthButtonLabel();

  protected readonly hiddenSsoRevealed = signal(false);

  protected readonly showHiddenSso = computed(
    () => this.hiddenOAuthEnabled && this.hiddenSsoRevealed()
  );

  protected readonly showSsoSection = computed(
    () => this.primaryOAuthEnabled || this.showHiddenSso()
  );

  logoUrl = '';
  showPassword = false;

  constructor() {
    this.logoUrl = this.getAssetUrl('img/logo-combi-plus.svg');
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 's') {
      event.preventDefault();
      if (this.hiddenOAuthEnabled) {
        this.hiddenSsoRevealed.set(true);
      }
    }
  }

  protected async onSubmit(): Promise<void> {
    await submit(this.loginForm, async () => {
      this.loginFormStore.submitLogin();
      return undefined;
    });
  }

  protected onPrimarySsoLogin(): void {
    this.loginFormStore.initiateOAuthLogin('primary');
  }

  protected onHiddenSsoLogin(): void {
    this.loginFormStore.initiateOAuthLogin('hidden');
  }

  protected togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  private getAssetUrl(path: string): string {
    return `${this.location.prepareExternalUrl('')}assets/${path}`;
  }
}
